import { useGetMandatoryUserAgreementsHandler } from "@via/pages/authentication/self-service-signup/authentication.api.tsx";

/**
 * The steps of self sign-up and of the Upgrade Your Account flow, in the order they run.
 * A flow lists a subset of these. Tracking the current step by key rather than by index keeps the
 * right step on screen when a step is dropped from the list, for example the User Agreement step
 * on an install with no active agreement (#989).
 */
export type OnboardingStepKey =
  | "account"
  | "agreement"
  | "payment"
  | "confirmation";

const STEP_ORDER: OnboardingStepKey[] = [
  "account",
  "agreement",
  "payment",
  "confirmation",
];

/**
 * Index of `target` in `keys`. When `target` is not listed, the index of the first listed step
 * that runs after it, so a dropped step resolves to the step that follows it.
 */
export const resolveStepIndex = (
  keys: OnboardingStepKey[],
  target: OnboardingStepKey
): number => {
  const order = STEP_ORDER.indexOf(target);
  const index = keys.findIndex(key => STEP_ORDER.indexOf(key) >= order);
  return index === -1 ? keys.length - 1 : index;
};

/**
 * Whether the User Agreement step can be skipped. It is skipped only once the agreements request
 * succeeded with an empty list. While the request is pending, disabled or failed, the step stays,
 * because an error is not the same as having no agreement.
 */
export const useAgreementStep = (enabled: boolean) => {
  const { data, isPending, isSuccess, dataUpdatedAt, errorUpdatedAt } =
    useGetMandatoryUserAgreementsHandler({ enabled });
  // Loading means the first answer has not arrived yet. After a failure, a remounted observer
  // reports pending again while it refetches; counting that as loading would unmount the step
  // that shows the error, remount it, refetch and loop.
  const neverSettled = dataUpdatedAt === 0 && errorUpdatedAt === 0;
  return {
    skipAgreement: enabled && isSuccess && (data ?? []).length === 0,
    isAgreementLoading: enabled && isPending && neverSettled,
  };
};
